import { useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import useAppStore from '../store/useAppStore'
import { getTelegramStartParam, getRouteFromStartParam } from '../utils/startParamMapper'

/**
 * Компонент для обработки deep linking из Telegram
 * Перенаправляет пользователя на нужную страницу на основе start_param
 */
const DeepLinkHandler = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isFirstVisit, isAuthorized, isLoading } = useAppStore()
  const hasProcessedRef = useRef(false)

  useEffect(() => {
    // Обрабатываем deep link только один раз при монтировании
    if (hasProcessedRef.current || isAuthorized === null || isLoading || isFirstVisit === null) {
      return
    }

    // Получаем start_param из Telegram
    const startParam = getTelegramStartParam()

    if (!startParam) {
      hasProcessedRef.current = true
      return
    }

    console.log('[DeepLink] Received start_param:', startParam)

    // Преобразуем start_param в роут
    const targetRoute = getRouteFromStartParam(startParam)

    if (!targetRoute) {
      console.warn('[DeepLink] Invalid start_param, ignoring:', startParam)
      hasProcessedRef.current = true
      return
    }

    console.log('[DeepLink] Target route:', targetRoute)

    // Если пользователь первый раз заходит - приоритет у онбординга
    if (isFirstVisit) {
      console.log('[DeepLink] First visit detected, onboarding has priority')
      // Сохраняем целевой роут для редиректа после онбординга
      sessionStorage.setItem('pendingDeepLink', targetRoute)
      hasProcessedRef.current = true
      return
    }

    // Проверяем, не находимся ли мы уже на целевой странице
    // Учитываем query-параметры: сравниваем pathname + search
    const targetUrl = new URL(targetRoute, window.location.origin)
    const targetPath = targetUrl.pathname
    const targetSearch = targetUrl.search
    const currentSearch = location.search || ''
    if (location.pathname === targetPath && currentSearch === targetSearch) {
      console.log('[DeepLink] Already on target route, skipping navigation')
      hasProcessedRef.current = true
      return
    }

    // Выполняем навигацию на целевую страницу
    console.log('[DeepLink] Navigating to:', targetRoute)
    navigate(targetRoute, { replace: true })
    hasProcessedRef.current = true
  }, [navigate, location.pathname, location.search, isFirstVisit, isAuthorized, isLoading])

  // Проверяем наличие отложенного deep link после завершения онбординга
  useEffect(() => {
    // Если онбординг завершен и есть отложенный deep link
    if (isAuthorized !== true || isLoading || isFirstVisit !== false) return undefined

    const pendingDeepLink = sessionStorage.getItem('pendingDeepLink')
    if (!pendingDeepLink) return undefined

    console.log('[DeepLink] Processing pending deep link after onboarding:', pendingDeepLink)
    // Небольшая задержка для корректной работы навигации
    const timeout = setTimeout(() => {
      sessionStorage.removeItem('pendingDeepLink')
      navigate(pendingDeepLink, { replace: true })
    }, 100)
    return () => clearTimeout(timeout)
  }, [isFirstVisit, isAuthorized, isLoading, navigate])

  return children
}

export default DeepLinkHandler
